import React, { useState } from 'react';
import gql from 'graphql-tag';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    AppBar,
    Toolbar,
    Container,
    Card,
    CardContent,
    IconButton,
    Typography,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    Link
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EmptyPicture from '../../assets/empty_picture.png';
import { withAppContext } from '../../app-context';
import PasswordField from '../../components/PasswordField/PasswordField';
import { getGraphQLErrorMessage } from '../../utils/graphqlError';
import TransferBulletinsDialog from './TransferBulletinsDialog';

const FETCH_ME = gql`
    query{
        fetchMe {
            avatar
            document
            name
            email
            start
            finish
            school {
                id
                name
            }
        }
    }
`;

const UPDATE_ME = gql`
    mutation updateMe($email: String, $password: String) {
        updateMe(email: $email, password: $password)
    }
`;

const MIN_PASSWORD_LENGTH = 6;

const validationSchema = Yup.object().shape({
    email: Yup.string().trim().email('Informe um e-mail válido').required('Informe o e-mail'),
    changePassword: Yup.boolean(),
    password: Yup.string().when('changePassword', {
        is: true,
        then: Yup.string()
            .min(MIN_PASSWORD_LENGTH, `A senha deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres`)
            .required('Informe a nova senha'),
        otherwise: Yup.string()
    }),
    confirmPassword: Yup.string().when('changePassword', {
        is: true,
        then: Yup.string()
            .oneOf([Yup.ref('password')], 'As senhas não coincidem')
            .required('Confirme a nova senha'),
        otherwise: Yup.string()
    })
});

const buildInitialValues = fetchMe => ({
    email: fetchMe.email || '',
    changePassword: false,
    password: '',
    confirmPassword: ''
});

const fieldError = (formik, field) => (formik.touched[field] && formik.errors[field]) || undefined;

const ReadOnlyField = ({ label, value }) => (
    <TextField label={label} value={value || '-'} disabled fullWidth margin="normal" variant="outlined" />
);

const ProfileHeader = ({ fetchMe }) => (
    <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <img
            src={fetchMe.avatar || EmptyPicture}
            alt="Foto de perfil"
            style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover' }}
        />
        <Typography variant="h6" style={{ marginTop: 8 }}>{fetchMe.name}</Typography>
    </div>
);

const SettingsPage = props => {

    const client = useApolloClient();
    const { loading, error, data } = useQuery(FETCH_ME);
    const [updateMe] = useMutation(UPDATE_ME);
    const [isTransferOpen, setIsTransferOpen] = useState(false);

    const { setMessage } = props.appContext;

    const handleLogout = async () => {
        localStorage.removeItem('token');
        localStorage.removeItem('chapter');
        // Descarta o cache do Apollo para o próximo login não ver dados deste aluno.
        await client.clearStore();
        props.history.push('/login');
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            await updateMe({
                variables: {
                    email: values.email.trim(),
                    ...(values.changePassword ? { password: values.password } : {})
                },
                refetchQueries: [{ query: FETCH_ME }]
            });
            resetForm({ ...values, email: values.email.trim(), changePassword: false, password: '', confirmPassword: '' });
            setMessage('Alterações salvas com sucesso.');
        } catch (updateError) {
            setMessage(getGraphQLErrorMessage(updateError, 'Não foi possível salvar as alterações. Tente novamente.'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><CircularProgress /></div>;
    if (error) return <Typography color="error" style={{ padding: 24 }}>Não foi possível carregar seu perfil. Tente novamente mais tarde.</Typography>;

    const { fetchMe } = data;

    return (
        <>
            <AppBar position="static" color="primary">
                <Toolbar style={{ backgroundColor: '#FFF', color: '#000', textAlign: 'left' }}>
                    <IconButton edge="start" color="inherit" aria-label="Voltar" onClick={() => props.history.goBack()}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" style={{ flexGrow: 1, marginLeft: 8 }}>
                        Editar perfil
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm" style={{ marginTop: 24, marginBottom: 24, textAlign: 'left' }}>
                <Card>
                    <CardContent>
                        <ProfileHeader fetchMe={fetchMe} />

                        <ReadOnlyField label="Unidade" value={fetchMe.school && fetchMe.school.name} />
                        <ReadOnlyField label="Código de matrícula" value={fetchMe.document} />
                        <Typography variant="body2" color="primary" style={{ margin: '4px 0 8px 14px' }}>
                            Matriculado em: {fetchMe.start || '-'}<br />
                            Expira em: {fetchMe.finish || '-'}
                        </Typography>
                        <ReadOnlyField label="Nome" value={fetchMe.name} />

                        <Formik initialValues={buildInitialValues(fetchMe)} validationSchema={validationSchema} onSubmit={handleSubmit}>
                            {formik => (
                                <form onSubmit={formik.handleSubmit} noValidate>
                                    <TextField
                                        label="E-mail"
                                        name="email"
                                        type="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(fieldError(formik, 'email'))}
                                        helperText={fieldError(formik, 'email')}
                                        autoComplete="email"
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                    />

                                    <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => setIsTransferOpen(true)}
                                        style={{ display: 'block', margin: '8px 0 16px' }}
                                    >
                                        Deseja transferir suas notas para esta conta?
                                    </Link>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="changePassword"
                                                color="primary"
                                                checked={formik.values.changePassword}
                                                onChange={event => {
                                                    formik.setFieldValue('changePassword', event.target.checked);
                                                    formik.setFieldValue('password', '');
                                                    formik.setFieldValue('confirmPassword', '');
                                                }}
                                            />
                                        }
                                        label="Deseja trocar sua senha?"
                                    />

                                    <PasswordField
                                        label="Nova senha"
                                        name="password"
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={!formik.values.changePassword}
                                        error={Boolean(fieldError(formik, 'password'))}
                                        helperText={fieldError(formik, 'password')}
                                        autoComplete="new-password"
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                    />
                                    <PasswordField
                                        label="Confirme a nova senha"
                                        name="confirmPassword"
                                        value={formik.values.confirmPassword}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={!formik.values.changePassword}
                                        error={Boolean(fieldError(formik, 'confirmPassword'))}
                                        helperText={fieldError(formik, 'confirmPassword')}
                                        autoComplete="new-password"
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                    />

                                    <Button
                                        type="submit"
                                        color="primary"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        disabled={formik.isSubmitting}
                                        style={{ marginTop: 16 }}
                                    >
                                        {formik.isSubmitting && <CircularProgress size={16} style={{ marginRight: 8 }} />}
                                        Salvar alterações
                                    </Button>
                                </form>
                            )}
                        </Formik>

                        <Button color="primary" fullWidth onClick={handleLogout} style={{ marginTop: 8 }}>
                            Sair
                        </Button>
                    </CardContent>
                </Card>
            </Container>

            <TransferBulletinsDialog
                open={isTransferOpen}
                onClose={() => setIsTransferOpen(false)}
                onSuccess={() => {
                    setIsTransferOpen(false);
                    setMessage('Transferência realizada com sucesso.');
                }}
                onError={setMessage}
            />
        </>
    );
}

export default withAppContext(SettingsPage);
